import { Button } from "@/components/Button";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";
import { HeaderTextBlock } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useConsentFormFields,
  usePostPrivacyEmail,
} from "@/requests/hooks/collect";
import { NAVIGATE_FORWARDS } from "@/utils/const";
import formatError from "@/utils/formatError";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InferType, object, string } from "yup";

let hasFormBeenSubmitted = false;

const formSchema = object({
  email: string()
    .email(t("emailOnboarding.privacyEmail.right.input.invalid"))
    .required(t("emailOnboarding.privacyEmail.right.input.required")),
});

type IPrivacyEmail = InferType<typeof formSchema>;

const PrivacyEmail = () => {
  const navigate = useNavigate();
  const { data: consentFormFields } = useConsentFormFields({
    queryKey: ["collect", "consentFormFields", "privacyEmail"],
  });
  const {
    mutate: sendPrivacyEmail,
    error,
    isPending,
    isSuccess: isPostPrivacyEmailSuccess,
  } = usePostPrivacyEmail();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setError,
    setValue,
  } = useForm<IPrivacyEmail>({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (!!consentFormFields?.privacyEmail)
      setValue("email", consentFormFields.privacyEmail);
  }, [consentFormFields?.privacyEmail]);

  useEffect(() => {
    if (hasFormBeenSubmitted) trigger();
  }, [watch("email")]);

  useEffect(() => {
    if (isPostPrivacyEmailSuccess) navigateToNextScreen();
  }, [isPostPrivacyEmailSuccess]);

  const navigateToNextScreen = () => {
    navigate(getOnboardingUrl("PRIVACY_EMAIL_SAVED"), {
      state: {
        direction: NAVIGATE_FORWARDS,
      },
    });
  };

  useEffect(() => {
    if (!error) return;

    const { codes } = formatError(error);

    if (codes?.includes("Email")) {
      setError("email", {
        message: t("emailOnboarding.privacyEmail.right.input.invalid"),
      });
    }
  }, [error]);

  const onClickSendPrivacyEmail = ({ email }: IPrivacyEmail) => {
    if (!hasFormBeenSubmitted) hasFormBeenSubmitted = true;

    if (
      !!consentFormFields?.privacyEmail &&
      consentFormFields.privacyEmail === email
    ) {
      navigateToNextScreen();
      return;
    }

    sendPrivacyEmail({
      privacyEmail: email,
      version: consentFormFields?.version,
    });
  };

  const renderDescriptionText = () => (
    <div className="mt-10">
      <Trans i18nKey="emailOnboarding.privacyEmail.left.header.description">
        one
        <span className="font-semibold text-secondary">two</span>
        three
      </Trans>
    </div>
  );

  const renderInput = () => (
    <Form
      id="privacy-email-form"
      onSubmit={handleSubmit(onClickSendPrivacyEmail)}>
      <Input
        {...register("email")}
        label={t("emailOnboarding.privacyEmail.right.input.label")}
        placeholder={t("emailOnboarding.privacyEmail.right.input.label")}
        helperText={errors?.email?.message}
        variant={errors?.email?.message ? "error" : undefined}
        testId="privacy-email-input"
        helperTextTestId="privacy-email-helper-text"
      />
    </Form>
  );

  const renderSubmitCTA = () => (
    <Button
      variant="primary"
      trackingId={{
        eventName: "onboarding.collect.consent-form.privacy-email.submit",
      }}
      form="privacy-email-form"
      loading={isPending}
      disabled={isPending || !!errors?.email?.message}
      testId="privacy-email-cta">
      {t("emailOnboarding.privacyEmail.right.submit")}
    </Button>
  );

  const renderRightPanel = () => (
    <div className="mt-20 flex flex-col items-start gap-20 md:mt-30 md:flex-row">
      <div className="w-full flex-grow md:w-[unset]">{renderInput()}</div>
      <div className="w-full flex-shrink-0 pt-[unset] md:w-[unset] md:pt-5">
        {renderSubmitCTA()}
      </div>
    </div>
  );

  const renderLeftPanel = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.privacyEmail.left.header.feature")}
      featureTextColor="primary"
      headingText={t("emailOnboarding.privacyEmail.left.header.heading")}
      headingTextColor="secondary"
      customHtml={renderDescriptionText()}
      customHtmlPosition="bottom"
      descriptionTextColor="secondary"
    />
  );

  return (
    <SplitLayout
      leftPanel={renderLeftPanel()}
      rightPanel={renderRightPanel()}
      backButtonPath={getOnboardingUrl("KLAVIYO_OPTIN_UPDATED")}
    />
  );
};

export default PrivacyEmail;
